import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchScanIfNeeded,
  fillAiScannerRoundMetadata,
  persistAiScannerCheckedSymbols,
  persistAiScannerRound,
  removeAiScannerRound,
  requestScanSummary,
  resetAiScannerAnswers,
  setAiScannerAnswer,
  startAiScannerSession
} from '../../actions/scan';
import { fetchSymbolIfNeeded } from '../../actions/symbol';
import HASHTAG_FILTER_CONFIG from '../../constants/hashtagFilterConfig';
import { getScansForSelection } from '../../constants/hashtagScanMap';
import INVESTMENT_QUESTIONS from '../../constants/investmentQuestions';
import SCORING_SCANS from '../../constants/scoringScans';
import closeIcon from '../../icons/Icon-AI-Scanner-Close.svg';
import aiScannerHashtag from '../../icons/Icon-AI-Scanner-Hashtag.svg';
import aiScannerLogo from '../../icons/Icon-AI-Scanner-Logo.svg';
import aiScannerMeigara from '../../icons/Icon-AI-Scanner-Wordmark-Meigara.svg';
import aiScannerMikke from '../../icons/Icon-AI-Scanner-Wordmark-Mikke.svg';
import {
  dismissAiScannerBar,
  isAiScannerBarDismissed
} from '../../utils/aiScannerBarDismissal';
import * as aiScannerSession from '../../utils/aiScannerSession';
import {
  filterSelectedHashtags,
  getRequiredScans
} from '../../utils/hashtagFilterEngine';
import { useFeatureToggle } from '../../utils/hooks/useFeatureToggle';
import useLocalize from '../../utils/hooks/useLocalize';
import useOnboardingSeen from '../../utils/hooks/useOnboardingSeen';
import {
  fillMissingResultMetadata,
  selectCandidates,
  selectCandidateSymbols
} from '../../utils/scanCandidates';
import {
  buildSearchRound,
  buildSummarizeRequest,
  normalizeHashtagLabel
} from '../../utils/summarizePayload';
import './AiScanner.scss';
import AiScannerCoachMark from './AiScannerCoachMark';
import HashtagChips, { HASHTAG_DEFINITIONS } from './HashtagChips';
import InvestmentQuestions from './InvestmentQuestions';
import ReSearchOptions, { RE_SEARCH_CHOICES } from './ReSearchOptions';
import ScanResults from './ScanResults';
import WelcomeBackScreen from './WelcomeBackScreen';

const BAR_FOOTER_GAP = 0;
const MOBILE_FOOTER_ID = 'gnavi-mobile-footer';
// STORY-024 (#734) AC C20095689: where ScansContainer wants the
// 「スクリーニング」-title entry point rendered. The button is portalled in from
// here so the overlay's open state and feature gate stay in one component and
// ScansContainer keeps owning nothing but the slot.
export const TITLE_TRIGGER_SLOT_ID = 'ai-scanner-title-trigger-slot';
const SHEET_PEEK = 44; // visible height when collapsed (px)
// Gap between the overlay's top edge and the expanded sheet (px). Small enough
// that the expanded sheet covers the ✕, as the design intends.
const SHEET_TOP_MARGIN = 12;

// STORY-015 (#729): the re-search block's radio-group namespace, and the id of
// the prompt its 「はい」/「いいえ」 options are labelled by. One live re-search
// block exists at a time, so a constant is enough here — the per-round
// namespacing that history needs is `ai-scanner-round-N`.
const RE_SEARCH_INSTANCE_ID = 'ai-scanner-research';
const RE_SEARCH_PROMPT_ID = `${RE_SEARCH_INSTANCE_ID}-prompt`;

// STORY-015 (#729): the chip ids behind a restored round.
//
// `selectedHashtagIds` is the round's own record and is trusted when present.
// Rounds persisted before that field existed carry only the localized LABELS,
// so those are mapped back through the very definitions that produced them.
// Without the fallback a session saved before this story resumes with an EMPTY
// selection, which `filterResult` reports as a hard zero rather than as
// loading — so 「いいえ」 lands on 「条件に一致する銘柄が見つかりませんでした」 with
// nothing to re-rank, under a header still showing the round's real count.
//
// A label that no longer matches any definition (copy changed since the round
// was saved) drops out, and a round whose labels all drop out behaves as it
// did before this story: an empty selection, and a warning that asks the
// member to pick hashtags again — which is the honest thing to say when the
// tags cannot be recovered.
const restoredHashtagIds = (round, localize) => {
  if (!round) return new Set();

  // Filtered against the definitions rather than trusted as stored. An id that
  // no longer names a chip — a hashtag retired or renamed in a later release,
  // or storage that was corrupted or hand-edited — would otherwise be handed
  // to `getRequiredScans`, which knows nothing about it: the round resumes
  // with a selection that matches no scan, a count of 0, and the
  // 「条件に一致する銘柄が見つかりませんでした」 warning, for a session that was
  // saved perfectly well. Dropping the unknown ids lets a round whose ids ALL
  // went stale fall through to the label recovery below and come back.
  const known = (round.selectedHashtagIds || []).filter((id) =>
    HASHTAG_DEFINITIONS.some((tag) => tag.id === id)
  );
  if (known.length > 0) return new Set(known);
  // Compared in the STORED form, not the raw chip copy: the chips read
  // 「# 誰もが知る身近な企業」 and the round carries 「#誰もが知る身近な企業」, so
  // matching the two directly finds nothing at all — silently, leaving the
  // selection empty and 「いいえ」 on the no-matches warning.
  const labels = new Set((round.selectedHashtags || []).map(({ tag }) => tag));
  return new Set(
    HASHTAG_DEFINITIONS.filter((tag) =>
      labels.has(normalizeHashtagLabel(localize(tag.labelKey)))
    ).map((tag) => tag.id)
  );
};

// The Q1/Q2 pair a finished round offers to reuse, or null when it has none
// (persisted before both were answered) and the next round must simply ask.
const reusableAnswers = (round) =>
  round && round.q1Answer && round.q2Answer
    ? { q1: round.q1Answer, q2: round.q2Answer }
    : null;

/**
 * STORY-028 (#739): fill in a frozen round's missing summary once it lands.
 *
 * Returns the list untouched (same reference) when no frozen round belongs to
 * this conversation, so the common case — the summary landing while its flow
 * is still the live one — costs no re-render of the history subtree.
 *
 * Deliberately does NOT skip a round that already has a summary. Only the
 * request that owns the round gets this far (`requestScanSummary` returns
 * nothing for a superseded one), so an arriving summary is always the newer
 * one: a member who saw a summary, changed an answer, then re-opened hashtags
 * froze the round carrying the OLD text, and this is what replaces it. A
 * "don't overwrite" guard here would leave that round showing a summary
 * localStorage no longer holds.
 *
 * @param {Array|null} rounds - The frozen rounds, or `null` before any exist.
 * @param {string} conversationId - The round the summary belongs to.
 * @param {string} summaryText - The accepted summary.
 * @returns {Array|null} The rounds, with that one round's `aiSummary` filled.
 */
function fillSummary(rounds, conversationId, summaryText) {
  if (!rounds) return rounds;
  const index = rounds.findIndex(
    (round) => round.conversationId === conversationId
  );
  if (index === -1) return rounds;
  if (rounds[index].aiSummary === summaryText) return rounds;
  const next = rounds.slice();
  next[index] = { ...next[index], aiSummary: summaryText };
  return next;
}

/**
 * AiScannerContainer Component
 *
 * Renders the final "銘柄ミッケ" bottom-bar entry point (STORY-022) at the
 * bottom of the Scanner page at the smartphone breakpoint. The bar shows the
 * 銘柄ミッケ logo, a subtitle and an ✕ button. Tapping the bar body opens the
 * AI Scanner chat overlay; the overlay can be dismissed via the ✕ icon at the
 * top-right.
 *
 * The bar floats fixed at the bottom of the viewport while the member scrolls.
 * Once the footer scrolls into view its `bottom` value is increased so it sits
 * directly on top of the footer instead of covering it, keeping the footer
 * fully visible and tappable at the end of the page.
 *
 * The ✕ hides the bar only (not the chat). The dismissal is remembered in a
 * cookie with a one-month expiry (see {@link dismissAiScannerBar}), so the bar
 * re-appears automatically once the cookie lapses.
 *
 * On the user's first open a two-step onboarding coach mark (see
 * {@link AiScannerCoachMark}) is layered on top of the main blank overlay.
 * The "seen" flag is persisted per user via {@link useOnboardingSeen} and is
 * only recorded when the user completes the onboarding (taps "AI Chatbot を
 * はじめる") or closes the overlay via ✕. Leaving mid-flow does not record
 * the flag, so the onboarding resumes from step 1 on the next visit.
 *
 * @returns {JSX.Element} The AI Scanner bottom bar and, when open, the overlay.
 */
export const AiScannerContainer = () => {
  const { isFeatureEnabled } = useFeatureToggle('aiScannerPopsicleEnabled');
  const { hasSeen, markSeen, userId } = useOnboardingSeen();
  const localize = useLocalize();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [barBottomOverride, setBarBottomOverride] = useState(null);
  // STORY-022: the bar is shown by default and stays until the member taps ✕.
  // The dismissal persists in a cookie (one-month expiry), read once on mount
  // so a returning member within the month never sees the bar re-appear.
  const [barDismissed, setBarDismissed] = useState(() => isAiScannerBarDismissed());
  // STORY-024 (#734): the ScansContainer-owned node the title entry point is
  // portalled into. Resolved after mount — the slot is not in the DOM yet on
  // this component's first render.
  const [titleTriggerSlot, setTitleTriggerSlot] = useState(null);
  // Locally track whether the coach mark is showing for the CURRENT open
  // session. This lets the "seen" flag be flipped in localStorage before we
  // unmount without immediately re-rendering the main chat mid-transition.
  const [showCoachMark, setShowCoachMark] = useState(false);
  // STORY-024 (#734): the hashtag link's centre, in overlay pixels, handed to
  // the coach mark so its step-2 spotlight lands on the link instead of on a
  // constant. Measured because the link's position depends on how much the
  // greeting and caution above it wrap: it sits at 252px in Japanese at 375px
  // wide, 278px at 320px wide, and 300px at 320px in English — a 48px spread
  // against a spotlight whose vertical radius is 21px, so a single constant
  // cannot cover it and the narrow/English cases missed the link entirely.
  // `null` until measured; the stylesheet keeps the Japanese 375px value as
  // its fallback.
  const [hashtagTargetY, setHashtagTargetY] = useState(null);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [sheetOffset, setSheetOffset] = useState(null); // px from top while dragging; null = snapped
  const [sheetDragging, setSheetDragging] = useState(false);
  // STORY-009: true once the member taps 「NNN 件を表示」 in the hashtag sheet.
  const [showQuestions, setShowQuestions] = useState(false);
  // STORY-015 (#729): the answer to 「…前回と同じ条件で進めますか？」 — one of
  // `RE_SEARCH_CHOICES`, or `null` while a second round is waiting on it.
  // Only meaningful once a previous round exists; a first round never asks.
  const [reSearchChoice, setReSearchChoice] = useState(null);
  // STORY-015 (#729): the previous round's Q1/Q2 pair, offered for reuse by
  // the re-search prompt, else null.
  //
  // Armed by `handleResume` on the restored session's last round, and re-armed
  // by `handleOpenHashtagSheet` on the round just finished — in a fresh session
  // as much as a resumed one. Only a session's first round has nothing to
  // offer, so it never asks.
  //
  // Note this is armed BEFORE the round it prompts on — on the resume or the
  // sheet opening — but only rendered once the member confirms a hashtag
  // selection: the prompt belongs to the round they are starting, not to the
  // one they just finished or are replaying.
  const [reSearchFrom, setReSearchFrom] = useState(null);
  // STORY-015 (#729): 「いいえ」 — the live Q1/Q2 are showing under the prompt.
  const isChangingConditions = reSearchChoice === RE_SEARCH_CHOICES.CHANGE;

  // STORY (#725): which state the overlay body shows on open — the
  // welcome-back screen (a saved session with >=1 round exists) or today's
  // chat. Re-decided fresh on every `handleOpen`, never persisted.
  const [screen, setScreen] = useState('chat');
  // The frozen flows currently on screen, oldest first — rendered as
  // question/result pairs above the live chat. Their content is fixed; only
  // the result cards' ticks (and so their registration) stay live.
  //
  // STORY (#725) seeded this from a resumed session; STORY-028 (#739) makes it
  // grow during a sitting too, as each completed flow is frozen by the
  // "re-open hashtags" affordance (FR-003/FR-004). Both sources feed ONE list
  // rather than two: a restored flow and a just-frozen flow are the same
  // thing — a `SearchRound` displayed read-only — and a second list would
  // duplicate the renderer, the ref, and the ordering guarantee. Appending
  // keeps this list in the same order as the persisted `searchRounds`.
  const [historyRounds, setHistoryRounds] = useState(null);
  // STORY (#725): the session `handleOpen` loaded for the welcome-back
  // screen, stashed so `handleResume` doesn't need a second `load()` call.
  const loadedSessionRef = useRef(null);
  const sheetRef = useRef(null);
  // STORY-024 (#734): the coach mark's step-2 spotlight aims at the hashtag
  // link, so both elements are measured rather than assumed — see
  // `hashtagTargetY` below.
  const overlayRef = useRef(null);
  const hashtagLinkRef = useRef(null);
  const questionsRef = useRef(null);
  const resultsRef = useRef(null);
  // STORY (#725): a dedicated ref for "land on the latest history round" —
  // deliberately NOT `resultsRef`. The Rule 5 effect below tests
  // `resultsRef.current` truthiness as a proxy for "the live results div has
  // mounted"; sharing it with a history round (which mounts long before any
  // live results exist) would make that proxy permanently true and misdirect
  // every later scroll (e.g. selecting hashtags and reaching the questions
  // step) back to the old history summary instead of the questions anchor.
  // Anchored to the round's own container (not its `ScanResults` div) so
  // `block: 'start'` lands on the round's hashtag condition line — the
  // 「#タグ」の条件で探しました✨ header — rather than scrolling past it to the
  // results block underneath.
  const latestHistoryRef = useRef(null);
  // STORY-028 (#739): the `SearchRound` the live flow last assembled, so
  // `handleOpenHashtagSheet` can freeze the flow without rebuilding it from
  // store values that have since moved on (a background scan refresh, a
  // summary landing). `null` means "no completed flow to freeze" — the member
  // re-opened the sheet before answering Q2, or straight after a close.
  //
  // STORY-029 (#740): this decides what gets FROZEN, not whether the live flow
  // is torn down — `hasLiveResults` does that. The two differ for a flow whose
  // round could not be assembled; see the note on `hasLiveResults`.
  const liveRoundRef = useRef(null);
  const sheetExpandedRef = useRef(false);
  const dragRef = useRef({
    active: false, startY: 0, startOffset: 0, lastY: 0, lastT: 0, velocity: 0
  });

  // Helper to keep the expanded ref in sync for pointer event handlers
  const updateSheetExpanded = useCallback((value) => {
    sheetExpandedRef.current = value;
    setSheetExpanded(value);
  }, []);

  // STORY-009: tearing down the questions step is two steps, not one — the
  // answers live in the store rather than in this component, so clearing
  // `showQuestions` on its own would leave the pair behind. Kept together so
  // the callers (user switch, open, close, re-search) cannot drift apart.
  //
  // STORY-028 (#739) adds the assembled round to the same teardown: a round
  // left behind would be frozen a second time by the next re-open, dropping
  // the previous sitting's flow into the middle of a new one.
  const resetQuestionsFlow = useCallback(() => {
    setShowQuestions(false);
    liveRoundRef.current = null;
    // STORY-015 (#729): cleared with everything else, so a chat that starts
    // fresh (open with nothing to resume, or 「新しく始める」) carries no pair
    // to offer until its first round finishes. `handleOpenHashtagSheet`
    // re-arms it right after calling this on the pair that round used.
    setReSearchChoice(null);
    setReSearchFrom(null);
    dispatch(resetAiScannerAnswers());
  }, [dispatch]);

  // STORY (#725): `screen`, `historyRounds`, and `loadedSessionRef` together
  // represent one thing — "is there a resumable session offered/applied right
  // now" — and must always change as a unit. Centralized here so every
  // caller that means "no, forget about resuming" (user switch, close, a
  // fresh open/start-fresh) shares one place instead of three separately
  // hand-written `setScreen`/`setHistoryRounds`/`loadedSessionRef.current`
  // triples that could drift out of sync.
  const resetSessionScreen = useCallback(() => {
    setScreen('chat');
    setHistoryRounds(null);
    loadedSessionRef.current = null;
  }, []);

  // Reset the overlay when the active user changes (e.g., switching between
  // user sessions on the same device without a full page reload) so state
  // from the previous user's session does not carry over.
  useEffect(() => {
    setIsOpen(false);
    setShowCoachMark(false);
    updateSheetExpanded(false);
    setSheetOffset(null);
    setSheetDragging(false);
    // Unobservable today — `handleOpen` resets too, and the questions only
    // render after it — but the answers are session state like everything
    // else reset here.
    resetQuestionsFlow();
    // STORY (#725): a different user's saved session must not leak into this
    // one's next open — re-decided fresh by the next `handleOpen`.
    resetSessionScreen();
  }, [resetQuestionsFlow, resetSessionScreen, userId, updateSheetExpanded]);

  // The bar's `bottom`, raised once the footer scrolls into view so the bar
  // sits on top of the footer instead of covering it. rAF-throttled.
  useEffect(() => {
    if (!isFeatureEnabled) {
      return;
    }

    let rafId = null;

    const updateBarPosition = () => {
      const footer = document.getElementById(MOBILE_FOOTER_ID);
      if (!footer) return;

      const footerRect = footer.getBoundingClientRect();
      if (footerRect.top < window.innerHeight) {
        setBarBottomOverride(window.innerHeight - footerRect.top + BAR_FOOTER_GAP);
      } else {
        setBarBottomOverride(null);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        updateBarPosition();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateBarPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isFeatureEnabled]);

  // STORY-024 (#734) AC C20095689: resolve the 「スクリーニング」-title slot.
  // Done in an effect rather than at render because the node belongs to
  // ScansContainer and only exists once the whole tree has committed.
  useEffect(() => {
    if (!isFeatureEnabled) return;
    setTitleTriggerSlot(document.getElementById(TITLE_TRIGGER_SLOT_ID));
  }, [isFeatureEnabled]);

  // Freeze the background while the panel is up — it is `aria-modal`, so the
  // page behind it must not scroll. The scroll position is restored on close
  // so the member returns to the row they were looking at.
  useEffect(() => {
    if (!isOpen) return;

    const { scrollX, scrollY } = window;
    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousRootOverflow = documentElement.style.overflow;

    // Both elements: `body` alone leaves the scroll on the root in some
    // mobile browsers, which is exactly where this needs to hold.
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousRootOverflow;
      window.scrollTo(scrollX, scrollY);
    };
  }, [isOpen]);

  // STORY-024 (#734): measure the hashtag link for the coach mark's step-2
  // spotlight.
  useEffect(() => {
    if (!showCoachMark) return;

    const measure = () => {
      const overlay = overlayRef.current;
      const link = hashtagLinkRef.current;
      if (!overlay || !link) return;
      const overlayRect = overlay.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      setHashtagTargetY(
        Math.round(linkRect.top - overlayRect.top + linkRect.height / 2)
      );
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showCoachMark]);

  // STORY-008: Selection state feeds the live-count derivation further down.
  const [selectedHashtagIds, setSelectedHashtagIds] = useState(() => new Set());

  // STORY-013 (#713): the previous selection, so the change handler can tell an
  // ADD from a REMOVE. Held in a ref rather than read from `selectedHashtagIds`
  // so the callback keeps a stable identity (a new `onSelectionChange` would
  // re-render HashtagChips for nothing).
  const previousSelection = useRef(new Set());
  // STORY-015 (#729): the hashtag selection a resume just restored, waiting for
  // its scan pass. See `handleResume`.
  const pendingRestoreScansRef = useRef(null);

  // The selection lives here rather than in HashtagChips, so this is the one
  // place that clears it. `handleResume` resets mid-session without unmounting
  // the sheet, so a child-owned copy would survive this call and leave chips
  // looking selected against a container that holds none.
  //
  // STORY-029 (#740) FR-001 removed the third caller: re-opening hashtags no
  // longer clears, so the only clears left are the two that genuinely begin a
  // different conversation — a fresh chat and a resume.
  const resetHashtagSelection = useCallback(() => {
    previousSelection.current = new Set();
    setSelectedHashtagIds(new Set());
  }, []);

  // STORY (#725): the shared "start from a blank chat" reset — used both by
  // `handleOpen` when there's nothing to resume and by `handleStartFresh`
  // (after clearing the saved session), so the two paths cannot drift apart.
  const openFreshChat = useCallback(() => {
    resetHashtagSelection();
    // STORY-009: a fresh conversation, so neither the questions step nor the
    // previous answers carry over.
    resetQuestionsFlow();
    // Show the coach mark only for users who have not yet seen it on this
    // device. Returning users skip straight to the chat.
    setShowCoachMark(!hasSeen);
    resetSessionScreen();
  }, [hasSeen, resetHashtagSelection, resetQuestionsFlow, resetSessionScreen]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    // STORY (#725): decide once, up front, whether there's a session worth
    // offering to resume — the welcome-back screen only ever appears here.
    const savedSession = aiScannerSession.load();
    if (savedSession && savedSession.searchRounds.length > 0) {
      loadedSessionRef.current = savedSession;
      setScreen('welcomeBack');
      return;
    }
    openFreshChat();
  }, [openFreshChat]);

  // STORY (#725): 「新しく始める」 — clear the saved session, then start exactly
  // as `handleOpen` would have if there had been nothing to resume.
  const handleStartFresh = useCallback(() => {
    aiScannerSession.clear();
    openFreshChat();
  }, [openFreshChat]);

  // STORY (#725): 「前回の履歴を見る」 — hand the rounds `handleOpen` already
  // loaded to the chat view for read-only replay (rendering is US4/T018).
  const handleResume = useCallback(() => {
    const savedSession = loadedSessionRef.current;
    loadedSessionRef.current = null;
    resetHashtagSelection();
    resetQuestionsFlow();
    // The onboarding coach mark has nothing to do with a member who has a
    // history to resume — never layer it over the restored history.
    setShowCoachMark(false);
    const restored = (savedSession && savedSession.searchRounds) || [];
    setHistoryRounds(restored);
    const lastRound = restored[restored.length - 1];
    // STORY-015 (#729): the resumed session replays in full and asks nothing.
    // This only remembers the pair the NEXT round will offer to reuse, once
    // the member picks hashtags and taps 「NNN 件を表示」.
    setReSearchFrom(reusableAnswers(lastRound));
    // AC C20124844: 「いいえ」 re-ranks the SAME hashtags against a new pair, so
    // the selection behind the saved round has to come back with it.
    const restoredIds = restoredHashtagIds(lastRound, localize);
    previousSelection.current = restoredIds;
    setSelectedHashtagIds(restoredIds);
    // Seeding state directly bypasses `handleHashtagSelectionChange`, which is
    // what normally fires the scans, so the restored selection would filter
    // against nothing. Handed to the effect below `runScanFetchPass` rather
    // than called here: that callback is declared after this one, so naming it
    // in this dependency array would read it in its temporal dead zone.
    pendingRestoreScansRef.current = restoredIds;
    setScreen('chat');
  }, [localize, resetHashtagSelection, resetQuestionsFlow]);

  // STORY-022: ✕ on the bar hides the bar only — not the chat. Persist the
  // dismissal so the bar stays gone for the month, then drop it from the DOM.
  const handleDismiss = useCallback(() => {
    dismissAiScannerBar();
    setBarDismissed(true);
  }, []);

  const handleClose = useCallback(() => {
    // ✕ during onboarding counts as a completion for "seen" tracking, per
    // FR-007 / Rule 4. If the coach mark was already dismissed this session,
    // don't call markSeen again — the hook itself is idempotent, but skipping
    // the call keeps container behavior explicit and testable.
    if (showCoachMark) {
      markSeen();
    }
    setShowCoachMark(false);
    resetQuestionsFlow();
    setIsOpen(false);
    // STORY (#725): re-evaluate `load()` from scratch on the next open — ✕
    // is not a choice (US5) and must not clear or restore anything.
    resetSessionScreen();
  }, [markSeen, resetQuestionsFlow, resetSessionScreen, showCoachMark]);

  const handleCoachMarkComplete = useCallback(() => {
    markSeen();
    setShowCoachMark(false);
  }, [markSeen]);

  // --- Bottom sheet snap positions (relative to overlay top) ---
  const getSheetSnaps = useCallback(() => {
    // `sheetRef` is still null on the render that opens the panel, so the
    // fallback positions the first collapsed peek. The overlay is full-screen,
    // so the viewport height is its height.
    const h =
      sheetRef.current?.parentElement?.clientHeight || window.innerHeight;
    return { open: SHEET_TOP_MARGIN, closed: h - SHEET_PEEK };
  }, []);

  // Tapping the hashtag link programmatically expands the sheet.
  const handleExpandSheet = useCallback(() => {
    updateSheetExpanded(true);
    setSheetOffset(null);
  }, [updateSheetExpanded]);

  // STORY-013 (#713), generalized by STORY-010 (#726): scanName -> the
  // in-flight request promise, so two rapid passes needing the same scan share
  // one Syphon call instead of the second silently relying on the first to
  // land. `shouldFetchScan` deliberately does not consider in-flight state, so
  // this is the only place concurrent duplicates are collapsed. Shared by both
  // passes — the hashtag selection (STORY-007) and the scoring prefetch
  // (STORY-010) — so a scan one has in flight is never requested again by the
  // other.
  const inFlightScans = useRef(new Map());

  // Fire de-duplicated scan requests. Fire-and-forget by design: nothing awaits
  // the pass, so neither caller blocks on it. Success updates `scanData` and
  // failure updates `scanErrors`, and both re-render via useSelector, so no
  // render trigger is needed on settle.
  // User/token validation is handled inside fetchScan (see actions/scan.js).
  const runScanFetchPass = useCallback(
    (scanNames) => {
      const inFlight = inFlightScans.current;

      scanNames.forEach((scanName) => {
        if (inFlight.has(scanName)) return;

        // Freshness lives in the thunk (`shouldFetchScan`), so a scan that is
        // still fresh resolves without touching Syphon.
        const result = dispatch(fetchScanIfNeeded(scanName));
        const settled =
          result && typeof result.then === 'function'
            ? result
            : Promise.resolve(result);
        inFlight.set(scanName, settled);
        settled
          .finally(() => {
            inFlight.delete(scanName);
          })
          // `fetchScan` catches its own failures, so this only fires if
          // something throws inside that catch (e.g.
          // redirectToLoginIfRequired). Swallow it rather than emitting an
          // unhandled rejection.
          .catch(() => {});
      });
    },
    [dispatch]
  );

  // STORY-009: 「NNN 件を表示」 collapses the sheet and starts the question flow.
  //
  // STORY-010: it also starts the scoring-scan prefetch. The tap is the trigger
  // rather than the filter resolving, because each hashtag toggle changes the
  // pool and would re-issue the whole union; by the tap the member has
  // committed to a selection. The pass is not awaited, so the questions open
  // immediately whether the scans have landed, are in flight, or failed — the
  // ranking story falls back to fetch-then-rank in that case.
  //
  // Deliberately fired on EVERY tap rather than once per session: the 60s
  // freshness check in `fetchScanIfNeeded` already makes a repeat tap free, and
  // a session-level guard would suppress the refresh of data that has aged out.
  //
  // Scans the selection already filters on are EXCLUDED, and that exclusion is
  // load-bearing rather than an optimization. `fetchScanIfNeeded` refetches
  // anything past SCAN_STALE_SECONDS, and three scoring scans (`GrowthScore`,
  // `TotalScore`, `NetSalesChange`) are also hashtag scans — so refreshing them
  // here would re-run the filter with newer data and change
  // 「NNN件」 straight after the member tapped that very number. Which hashtags
  // that hit would depend on whether their scans happen to appear in
  // SCORING_SCANS, so the behaviour would differ per hashtag for no reason a
  // member could follow. Nothing is lost by skipping them: a required scan is
  // necessarily already cached, or the count could not have rendered. Making
  // the count refresh on tap is a deliberate product change, not this story.
  const handleShowResults = useCallback(() => {
    updateSheetExpanded(false);
    setSheetOffset(null);
    setShowQuestions(true);

    const alreadyFiltered = new Set(
      getRequiredScans(selectedHashtagIds, HASHTAG_FILTER_CONFIG)
    );
    runScanFetchPass(
      SCORING_SCANS.filter((scanName) => !alreadyFiltered.has(scanName))
    );
  }, [runScanFetchPass, selectedHashtagIds, updateSheetExpanded]);

  // STORY-008: memoized derivation of live count / loading / error.
  //
  // We deliberately do NOT wire this through a reselect selector — reading the
  // store slices with plain `useSelector` and deriving with `useMemo` below
  // keeps the data flow explicit and re-evaluates on every dependency change.
  const scanData = useSelector(
    (state) => (state && state.scans && state.scans.scanData) || null
  );
  const scanErrors = useSelector(
    (state) => (state && state.scans && state.scans.scanErrors) || null
  );

  // STORY-009: watched so the newest message can be scrolled into view when Q2
  // appears. The answers themselves belong to InvestmentQuestions.
  const answeredQ1 = useSelector(
    (state) =>
      (state && state.scans && state.scans.aiScannerAnswers &&
        state.scans.aiScannerAnswers.q1) || null
  );

  // STORY-013: Q2 both reveals the results step and scrolls it into view.
  const answeredQ2 = useSelector(
    (state) =>
      (state && state.scans && state.scans.aiScannerAnswers &&
        state.scans.aiScannerAnswers.q2) || null
  );

  // STORY-011 (#719): inputs for the summarize request. The session id is
  // minted when the questions step opens; the symbol metadata supplies the
  // `name` Syphon requires on every entry.
  const aiScannerSessionId = useSelector(
    (state) => (state && state.scans && state.scans.aiScannerSessionId) || null
  );
  const symbolMetadata = useSelector(
    (state) => (state && state.symbols && state.symbols.data) || null
  );

  // STORY-028 (#739): the summary text currently on screen. Read here rather
  // than left to `ScanResults` because freezing a flow has to capture what the
  // member was looking at: the round is assembled the instant Q2 is answered,
  // seconds before the summary lands, so a freeze built from the round alone
  // would blank out a summary they had been reading.
  const liveSummaryText = useSelector(
    (state) =>
      (state && state.scans && state.scans.aiSummary &&
        state.scans.aiSummary.text) || null
  );

  // STORY-009: hashtag labels for the header message, in selection order.
  const selectedTagLabels = useMemo(
    () =>
      Array.from(selectedHashtagIds)
        .map((id) => HASHTAG_DEFINITIONS.find((tag) => tag.id === id))
        .filter(Boolean)
        .map((tag) => localize(tag.labelKey)),
    [localize, selectedHashtagIds]
  );

  // Rule 5: keep the latest message visible as the conversation grows.
  useEffect(() => {
    if (!showQuestions) return;
    // Q2 ends the conversation, so the results are read from the top rather
    // than tailed like a question.
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start'
      });
      return;
    }
    questionsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [answeredQ1, answeredQ2, showQuestions]);

  // STORY (#725): landing on the latest round after 「前回の履歴を見る」 (FR-010).
  // Distinct from the Rule 5 effect above — restoring never sets
  // `showQuestions`, so that effect's guard would otherwise skip this.
  //
  // STORY-028 (#739): freezing a flow grows `historyRounds`, so this same
  // effect re-fires and puts the just-frozen flow's condition line at the top
  // of the viewport — with the re-open affordance and the expanding sheet
  // directly below it. No second scroll effect is needed.
  useEffect(() => {
    if (!historyRounds || historyRounds.length === 0) return;
    latestHistoryRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [historyRounds]);

  const hasHistoryRounds = Boolean(historyRounds && historyRounds.length);

  // STORY-007: Fire de-duplicated scan requests when hashtag selection changes.
  const handleHashtagSelectionChange = useCallback(
    (selectedIds) => {
      const nextSelection = new Set(selectedIds);
      const previous = previousSelection.current;
      const selectionGrew = Array.from(nextSelection).some(
        (id) => !previous.has(id)
      );

      previousSelection.current = nextSelection;
      setSelectedHashtagIds(nextSelection);

      // STORY-013 (#713) AC C19820799 / C19820801: deselecting issues no
      // requests at all. The scans required by a smaller selection are always
      // a SUBSET of those required by the larger one, so a removal can never
      // produce a cache miss — the only thing it could trigger is a staleness
      // refetch of data we are already displaying. Skipping the pass entirely
      // keeps those ACs true regardless of how long the user took.
      //
      // Opening the panel is not a separate trigger either way. #713 could
      // say so because `resetHashtagSelection` emptied the selection on every
      // open, making the first toggle necessarily an ADD; STORY-029 (#740)
      // FR-001 stopped clearing it on a re-open, so the first toggle there can
      // just as well be a REMOVE. Both are still classified correctly —
      // nothing on that path touches `previousSelection`, so it stays equal to
      // `selectedHashtagIds` — and a REMOVE still issues nothing, by the
      // subset argument above.
      if (!selectionGrew) return;

      // Fire scan requests immediately (STORY-007 behavior preserved). The
      // whole selection is offered to `fetchScanIfNeeded`, not just the newly
      // required scans, so anything that has gone stale is refreshed too.
      const scanNames = getScansForSelection(nextSelection);
      if (scanNames.length === 0) return;

      runScanFetchPass(scanNames);
    },
    [runScanFetchPass]
  );

  const filterResult = useMemo(() => {
    const empty = {
      count: 0,
      symbols: [],
      isLoading: false,
      hasError: false,
      missingScans: []
    };
    if (!selectedHashtagIds || selectedHashtagIds.size === 0) return empty;

    const data = scanData || {};
    const errors = scanErrors || {};
    const required = getRequiredScans(
      selectedHashtagIds,
      HASHTAG_FILTER_CONFIG
    );

    // STORY-013 (#713): "no usable data yet" is exactly the loading condition,
    // so no separate in-flight check is needed and the count never regresses to
    // `-` for data we already hold — a stale scan keeps showing its previous
    // count while the refresh is in flight.
    //
    // Tests `.symbols`, not just presence, to stay in step with the other two
    // places that ask the same question: `shouldFetchScan` (actions/scan.js)
    // and `shouldFetchField` (desktopScans/ScanResultsSection.js). An entry
    // without symbols must read as "still loading", not as a zero-match result.
    const missing = required.filter((s) => !data[s.toLowerCase()]?.symbols);

    // Errors are only fatal for scans with nothing cached. `shouldFetchScan`
    // refetches a scan once it passes SCAN_STALE_SECONDS, so a scan whose
    // symbols are already on screen can pick up an error from a *refresh* that
    // failed. Checking `required` here instead of `missing` let that discard a
    // count the member was already looking at, and disable the submit button
    // with usable data still in the store — extending the promise in the
    // comment above from "refresh in flight" to "refresh failed".
    if (missing.some((s) => errors[s.toLowerCase()])) {
      return {
        count: null,
        symbols: [],
        isLoading: false,
        hasError: true,
        missingScans: []
      };
    }

    if (missing.length > 0) {
      return {
        count: null,
        symbols: [],
        isLoading: true,
        hasError: false,
        missingScans: missing
      };
    }

    const symbols = filterSelectedHashtags(
      data,
      selectedHashtagIds,
      HASHTAG_FILTER_CONFIG
    );
    return {
      count: symbols.length,
      symbols,
      isLoading: false,
      hasError: false,
      missingScans: []
    };
  }, [scanData, scanErrors, selectedHashtagIds]);

  // STORY-011 (#719): entering the questions step opens a conversation.
  //
  // The symbol names are prefetched HERE rather than at Q2 because
  // `fetchSymbolIfNeeded` batches through a debounce timer and cannot be
  // awaited — firing them while the member reads Q1 leaves several seconds for
  // the batch to land, so `name` is in the store by the time the payload is
  // assembled. A symbol still missing one is dropped by the assembler rather
  // than sent with an empty name, which Syphon rejects.
  useEffect(() => {
    if (!showQuestions) return;
    dispatch(startAiScannerSession());
    // Keyed on `showQuestions` alone: the conversation belongs to the step,
    // not to the candidate list, which keeps re-deriving as scans land.
    //
    // STORY-015 (#729): every round reaches this step through a confirmed
    // sheet, re-search included, so that one key covers them all — and each
    // round gets its own id, which is what `persistAiScannerRound` matches on
    // to append rather than overwrite a previous round.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, showQuestions]);

  // #736: the answer pairs this round could still end on — every combination
  // consistent with what has been answered so far. Nine before Q1, three once
  // Q1 lands, one once Q2 does.
  //
  // This is what makes the name prefetch below both correct and cheap. Ranking
  // depends on Q2, so the final five are not known until the member answers
  // it — but the summarize request is assembled in that same tick, far too
  // soon for a fetch started then to have landed. Prefetching only the pool's
  // head (which is MarketCap order, not rank order) would have left the actual
  // candidates nameless: dropped from the payload by `buildSymbols`, and
  // rendered as raw tickers on the cards.
  //
  // Enumerating instead of widening keeps it BOUNDED and exact. The union is
  // at most 45 symbols before Q1 and 15 after it, against a batch size of 25 —
  // one or two requests, for a guarantee that whichever answer the member
  // picks, its five candidates already have names.
  const openAnswerPairs = useMemo(() => {
    const codesFor = (questionId, answered) => {
      if (answered) return [answered];
      const question = INVESTMENT_QUESTIONS.find((q) => q.id === questionId);
      return question.options.map((option) => option.code);
    };

    const pairs = [];
    codesFor('q1', answeredQ1).forEach((q1) => {
      codesFor('q2', answeredQ2).forEach((q2) => {
        pairs.push({ q1: q1, q2: q2 });
      });
    });
    return pairs;
  }, [answeredQ1, answeredQ2]);

  // Every symbol that could end up on a card, de-duplicated. Recomputed as the
  // scoring scans land, so a pass that ran against an empty cache is redone
  // with real data rather than leaving the wrong names prefetched.
  const prefetchSymbols = useMemo(() => {
    // Gated on the step being open for the same reason the effect below is:
    // this ranks the pool once per open pair, and every hashtag toggle in the
    // sheet changes `filterResult.symbols`. Before the member has committed to
    // a selection there is nothing to prefetch names for.
    if (!showQuestions) return [];

    const union = new Set();
    openAnswerPairs.forEach((answers) => {
      selectCandidateSymbols(filterResult.symbols, {
        answers: answers,
        scanData: scanData
      }).forEach((symbolId) => union.add(symbolId));
    });
    return Array.from(union);
  }, [filterResult.symbols, openAnswerPairs, scanData, showQuestions]);

  useEffect(() => {
    if (!showQuestions) return;
    // Re-runs when the candidate set changes: a snapshot taken once on entry
    // could name a different five than the payload ends up carrying, and a
    // candidate with no name is skipped outright rather than backfilled — so
    // the two lists have to stay in step or the request silently goes short.
    // `fetchSymbolIfNeeded` is a no-op for anything already held, so the
    // repeats a re-run brings cost nothing.
    prefetchSymbols.forEach((symbolId) => {
      dispatch(fetchSymbolIfNeeded(symbolId));
    });
  }, [dispatch, showQuestions, prefetchSymbols]);

  // #736: the ranked candidates on screen. `ScanResults` is handed these
  // rather than the pool, so the cards and the summarize request are built
  // from one ranking rather than two that could disagree.
  const liveCandidates = useMemo(
    () =>
      selectCandidates(filterResult.symbols, {
        answers: { q1: answeredQ1, q2: answeredQ2 },
        scanData: scanData,
        symbolMetadata: symbolMetadata,
        localize: localize
      }),
    [answeredQ1, answeredQ2, filterResult.symbols, localize, scanData, symbolMetadata]
  );

  // Metadata landing after Q2 updates the live cards but not the round saved
  // at Q2, so it is copied onto the round for history and the next freeze.
  useEffect(() => {
    const live = liveRoundRef.current;
    if (!live) return;
    const results = fillMissingResultMetadata(live.results, liveCandidates);
    if (results === live.results) return;
    liveRoundRef.current = { ...live, results };
    dispatch(fillAiScannerRoundMetadata(live.conversationId, liveCandidates));
  }, [dispatch, liveCandidates]);

  // STORY-011 (#719): both questions answered — assemble and send. Also fires
  // when Q1 changes after Q2, re-requesting while an earlier call may be in
  // flight; the reducer drops any result superseded by a newer request.
  //
  // STORY (#724): the round to persist is assembled from the SAME body, so
  // stored hashtags/results always match the summary. `requestScanSummary`
  // persists it only when the summary is accepted (see that thunk); `results`
  // from `body.symbols` reuses the summarizer's name resolution.
  const handleAnswersComplete = useCallback(
    (answers) => {
      // #736: ranked from the `answers` the callback CARRIES, not from
      // `liveCandidates`. The answer that completes the pair was dispatched
      // moments ago and this closure still holds the previous render's store
      // values, so ranking off the memo would score the round on the pair as
      // it stood one answer ago. `selectCandidates` is pure, so re-running it
      // here is just a second call on the same inputs the next render uses —
      // the two agree by construction.
      // STORY-024 (#734): the full candidates rather than `selectCandidateSymbols`,
      // so the ordering sent to the summarizer and the industry codes stored on
      // the round come out of ONE ranking call and cannot disagree. Ranking is
      // driven by `answers`/`scanData` alone, so passing `symbolMetadata` adds
      // the industry code without touching the order.
      const ranked = selectCandidates(filterResult.symbols, {
        answers: answers,
        scanData: scanData,
        symbolMetadata: symbolMetadata
      });
      const body = buildSummarizeRequest({
        sessionId: aiScannerSessionId,
        answers: answers,
        selectedHashtagIds: selectedHashtagIds,
        symbols: ranked.map(({ symbol }) => symbol),
        scanData: scanData,
        symbolMetadata: symbolMetadata,
        localize: localize
      });
      const round = buildSearchRound(
        body,
        answers,
        aiScannerSessionId,
        filterResult.count,
        Object.fromEntries(ranked.map((candidate) => [candidate.symbol, candidate])),
        // STORY-015 (#729): stored so 「いいえ」 can search again on these same
        // hashtags after a resume — `selectedHashtags` keeps only the localized
        // labels, which cannot be turned back into a selection.
        selectedHashtagIds
      );

      // STORY-028 (#739) FR-001: persist the flow HERE, the moment the results
      // are reached, rather than leaving every write to the summary thunk.
      // Until now the only write was in `requestScanSummary`'s success branch,
      // so a member looking at their five cards while Bedrock 500s (or the
      // request never left the device) had nothing saved at all.
      //
      // It cannot duplicate the summary write: both carry the same
      // `conversationId`, and `persistAiScannerRound` replaces the stored
      // round carrying it — so this one appends and the summary one updates it
      // in place, filling in the `aiSummary` this round leaves null. A round
      // that could not be assembled (`body` null) is not persisted, matching
      // the rule #724 already applies.
      //
      // Assigned unconditionally: a re-emit that cannot be assembled (Rule 4
      // re-fires this on a changed Q1, and the selection may have been emptied
      // in the sheet since) must CLEAR the ref, not leave the previous round
      // behind for the next re-open to freeze a second time.
      //
      // A re-emit for the same conversation leaves the cards (and their ticks)
      // mounted, so the rebuilt round has to keep the ticks too — the freeze
      // copies this ref into history.
      const previous = liveRoundRef.current;
      liveRoundRef.current =
        round &&
        previous &&
        previous.conversationId === round.conversationId &&
        previous.checkedSymbols
          ? { ...round, checkedSymbols: previous.checkedSymbols }
          : round;
      if (round) {
        dispatch(persistAiScannerRound(round));
      }

      // STORY-028 (#739): the summary can land after the member has already
      // re-opened hashtags, by which point this round is frozen in
      // `historyRounds` — rendered from its own `aiSummary`, which the eager
      // write above left null. The thunk updates the PERSISTED round (see
      // `summaryRequestIdByConversation`); this fills in the copy on screen so
      // the frozen flow doesn't sit on the placeholder until the next reload.
      // A no-op while the round is still live: nothing in `historyRounds`
      // carries its `conversationId` yet.
      const conversationId = round && round.conversationId;
      // `Promise.resolve` because the thunk short-circuits to `false` when the
      // request never leaves the device.
      Promise.resolve(dispatch(requestScanSummary(body, round)))
        .then((result) => {
          const summaryText = result && result.summaryText;
          if (!conversationId || !summaryText) return;
          setHistoryRounds((current) =>
            fillSummary(current, conversationId, summaryText)
          );
        })
        // `requestScanSummary` catches its own failures, so this only fires if
        // something throws inside that catch (e.g. redirectToLoginIfRequired
        // on a rejection that carries no `code`). Swallow it rather than
        // emitting an unhandled rejection — same as `runScanFetchPass` above.
        .catch(() => {});
    },
    [
      aiScannerSessionId,
      dispatch,
      filterResult.symbols,
      localize,
      scanData,
      selectedHashtagIds,
      symbolMetadata,
      filterResult.count
    ]
  );

  // The ticks ride on the live round as well as in storage, because freezing
  // the flow into history copies the round from `liveRoundRef`.
  const handleLiveSelectionChange = useCallback(
    (checkedSymbols) => {
      const live = liveRoundRef.current;
      if (!live) return;
      liveRoundRef.current = { ...live, checkedSymbols };
      dispatch(
        persistAiScannerCheckedSymbols(live.conversationId, checkedSymbols)
      );
    },
    [dispatch]
  );

  // Is a completed flow on screen right now? Exactly the condition the results
  // block renders under (see the JSX below, which reads this same flag), so
  // "the member is looking at results" and "the re-open affordance exists" are
  // one fact rather than two that can disagree.
  //
  // STORY-029 (#740): this, NOT `liveRoundRef.current`, is what decides whether
  // opening the sheet tears the live flow down. The two come apart in one case
  // and it matters: a round that could not be assembled (no resolved names)
  // leaves the ref null while the results stay on screen. Guarding on the ref
  // would leave that flow live, and the next 「NNN件を表示」 would re-run its
  // conversation in place — the exact FR-002 defect, in the corner case.
  const hasLiveResults =
    showQuestions &&
    Boolean(answeredQ1) &&
    Boolean(answeredQ2) &&
    filterResult.symbols.length > 0;

  // STORY-028 (#739) FR-002/FR-003/FR-004: 「異なるハッシュタグを選ぶことで、
  // 違う銘柄を探すことができます！」 — the re-open affordance under a completed
  // flow's results.
  //
  // STORY-029 (#740) FR-003 makes this the ONE way the hashtag sheet opens,
  // for every trigger: the link under the results, the link above the sheet,
  // and the swipe-up/tap/Enter on the sheet's own handle. #739 gave the link
  // this behavior and left the handle calling `updateSheetExpanded` directly,
  // so a member who swiped instead of tapping kept the live flow and re-ran
  // the SAME conversation on confirm — rewriting the results they were
  // looking at instead of searching again below them (FR-002). Sharing the
  // handler is the fix; two handlers kept in step by hand is the bug.
  //
  // What makes that safe for every trigger is the `hasLiveResults` guard
  // below: with no completed flow on screen this is exactly today's plain
  // expand. So swiping the sheet up mid-flow, at Q1, still just opens it —
  // mid-flow re-selection is STORY-016 and deliberately not this story.
  //
  // This starts an ENTIRELY NEW flow rather than editing the current one: the
  // completed flow is frozen into `historyRounds` (where its content renders
  // read-only, like a resumed one), the live flow is torn down, and the same bottom sheet
  // the first round used re-opens. The new flow's questions/results then
  // append BELOW the frozen one.
  //
  // STORY-029 (#740) FR-001: it re-opens on the member's CURRENT hashtags,
  // green, rather than on a clean selection. #739 cleared them here, which
  // read as "start over" under results the member had just narrowed down —
  // and left a disabled 「0 件を表示」 as the only thing in the panel. The
  // selection seeds ITSELF, and NOT clearing it is the whole mechanism:
  // `selectedHashtagIds` already holds the committed set, because
  // `HashtagChips` is fully controlled and its only writer is reachable
  // solely while the sheet is open. So no explicit seeding step exists or is
  // needed. (The frozen round is no use as a source anyway — it stores
  // localized tag LABELS, not chip ids.)
  //
  // Frozen with the summary text currently on screen rather than the round's
  // own `aiSummary` (which is null until the summary lands — see
  // `handleAnswersComplete`), so the frozen flow shows exactly what the member
  // was reading.
  //
  // Two things it deliberately does NOT do:
  //  - it never calls `aiScannerSession.clear()`. The frozen flow is already
  //    in localStorage from the eager write, and the whole point of FR-005 is
  //    that the history survives starting another flow.
  //  - it never mints a `conversationId`. The existing `showQuestions` effect
  //    does that when the member confirms the new selection, and because that
  //    id differs from the frozen flow's, `persistAiScannerRound` APPENDS the
  //    next round instead of replacing the last one. Minting one here would
  //    leave a dangling id for a flow the member may abandon in the sheet.
  const handleOpenHashtagSheet = useCallback(() => {
    // Everything inside this branch is what makes an open a RE-open; outside
    // it, the call is today's plain expand.
    //
    // STORY-029 (#740) put the teardown behind this guard. #739 ran it
    // unconditionally because only the under-the-results link reached here,
    // and that link cannot render before a flow completes. The sheet's handle
    // can — and throwing away a half-answered questions step because the
    // member swiped the sheet up would lose answers they never discarded
    // (mid-flow re-selection is STORY-016, not this story).
    if (hasLiveResults) {
      // Null when the round could not be assembled — see `hasLiveResults`.
      // There is then nothing to freeze, but the flow is still finished and
      // must still be torn down, which is why the two are nested rather than
      // written as one condition.
      const completed = liveRoundRef.current;
      if (completed) {
        const frozen = { ...completed, aiSummary: liveSummaryText };
        setHistoryRounds((current) => (current || []).concat(frozen));
      }
      // Clears the answers, the session id, the summary AND `liveRoundRef` —
      // but deliberately NOT the hashtag selection (FR-001, above).
      // STORY-015 (#729): `resetQuestionsFlow` clears the re-search state with
      // everything else, so the next round re-arms here on the pair the member
      // just finished with — in a new session as much as a resumed one, and
      // 「前回の」 means the most recent answers, not the ones a resume loaded.
      // A round that could not be assembled leaves the existing pair standing.
      const finishedWith = reusableAnswers(completed);
      resetQuestionsFlow();
      setReSearchFrom(finishedWith || reSearchFrom);
    }
    // STORY-015 (#729), edge case: opening the sheet with the prompt unanswered
    // (or answered 「いいえ」 and left) means the member is changing the scan
    // results underneath it, so the question has to be put again against the
    // new ones. Only the CHOICE resets — `reSearchFrom` stands, because the
    // saved pair on offer has not changed.
    setReSearchChoice(null);
    // STORY-015 (#729): the scans behind a selection `handleResume` restored.
    // Seeding that selection as state skips `handleHashtagSelectionChange` and
    // so the fetch it normally fires, which would leave the sheet counting
    // against no data. Deliberately here and not on the resume itself: STORY
    // (#725) is explicit that restoring history requests NOTHING, and opening
    // the sheet is the first moment the member is heading for a new search.
    // Draining the ref keeps it to that first open.
    const restoredIds = pendingRestoreScansRef.current;
    if (restoredIds) {
      pendingRestoreScansRef.current = null;
      const scanNames = getScansForSelection(restoredIds);
      if (scanNames.length > 0) runScanFetchPass(scanNames);
    }
    handleExpandSheet();
  }, [
    handleExpandSheet,
    hasLiveResults,
    liveSummaryText,
    reSearchFrom,
    resetQuestionsFlow,
    runScanFetchPass
  ]);

  // STORY-015 (#729): answering 「…前回と同じ条件で進めますか？」.
  //
  // 「はい」 replays the saved pair into the store and hands off immediately.
  // The round it starts is a REAL search: the candidates are the ones the
  // member has just selected in the sheet, only the Q1/Q2 pair is carried over
  // — so a new request goes to Syphon and a new summary and cards come back.
  // Replaying through the store is what "reuse without asking again" means
  // here: every consumer downstream reads the pair from there, so a reused
  // pair and a freshly answered one are indistinguishable past this point.
  // `handleAnswersComplete` is called directly because the questions component
  // never renders interactively on this path and so never fires the hand-off.
  //
  // 「いいえ」 only records the choice: the live Q1/Q2 it reveals answer
  // themselves, through the same hand-off a first round uses.
  const handleReSearchChoice = useCallback(
    (choice) => {
      setReSearchChoice(choice);
      if (choice === RE_SEARCH_CHOICES.REUSE) {
        if (!reSearchFrom) return;
        dispatch(setAiScannerAnswer('q1', reSearchFrom.q1));
        dispatch(setAiScannerAnswer('q2', reSearchFrom.q2));
        handleAnswersComplete(reSearchFrom);
        return;
      }

      // Changing the answer to the prompt has to undo the round the previous
      // answer started. Switching 「はい」 → 「いいえ」 without this leaves the
      // reused pair selected in the questions the member was just told they
      // could change, and leaves the summary and cards 「はい」 produced sitting
      // under them as if they answered the new pair.
      //
      // Clearing the two answers is what does it: Q1 alone is shown while Q1
      // is null (STORY-009), and `hasLiveResults` needs both, so the results
      // come down with them. `resetAiScannerAnswers` would do the same and
      // also null the session id, which is the round's `conversationId` —
      // keeping it is deliberate, so the round the member ends up with
      // REPLACES the 「はい」 round in storage rather than appending a second
      // one for a search they abandoned.
      dispatch(setAiScannerAnswer('q1', null));
      dispatch(setAiScannerAnswer('q2', null));

      // ...and the round 「はい」 started is already IN storage: results are
      // persisted the moment they are reached (STORY-028 #739 FR-001), before
      // the summary lands. Taking it off screen is not enough — left stored,
      // a member who switches to 「いいえ」 and closes the scanner without
      // answering finds the search they backed out of replayed as history.
      // Keeping the `conversationId` would only overwrite it if they DO
      // finish, which is exactly the case this one is not.
      const abandoned = liveRoundRef.current;
      liveRoundRef.current = null;
      if (abandoned) dispatch(removeAiScannerRound(abandoned.conversationId));
    },
    [dispatch, handleAnswersComplete, reSearchFrom]
  );

  // STORY (#725): built once per `historyRounds` change, not on every
  // render — the history is read-only and never changes while an
  // unrelated live-chat re-render happens (e.g. dragging the hashtag sheet
  // fires `setSheetOffset` at pointer-move frequency). Memoizing the
  // elements (not just the data) lets React bail out of reconciling this
  // subtree on those unrelated renders.
  const historyRoundsElements = useMemo(() => {
    if (!historyRounds) return null;
    return historyRounds.map((round, index) => {
      const isLast = index === historyRounds.length - 1;
      return (
        <div
          key={round.conversationId || index}
          className="ai-scanner-questions-anchor"
          ref={isLast ? latestHistoryRef : undefined}
        >
          {/*
            Every round needs its OWN radio group (STORY-024 #734, and the
            same conclusion STORY-028 #739 reached independently): radios
            sharing a `name` form one group per document, so without this
            answering the live round's Q1 visually clears every frozen
            round's Q1, and each round repeats the prompt `id` that its
            `aria-labelledby` points at. The index is unique within this
            one append-only list; the live round keeps the unscoped default.
          */}
          <InvestmentQuestions
            readOnly
            instanceId={`ai-scanner-round-${index}`}
            overrideAnswers={{ q1: round.q1Answer, q2: round.q2Answer }}
            selectedTags={round.selectedHashtags.map((h) => h.tag)}
            matchCount={round.matchCount}
          />
          <div>
            <ScanResults
              overrideResults={round.results}
              overrideSummaryText={round.aiSummary}
              initialSelected={round.checkedSymbols}
              onSelectionChange={
                round.conversationId
                  ? (checkedSymbols) =>
                      dispatch(
                        persistAiScannerCheckedSymbols(
                          round.conversationId,
                          checkedSymbols
                        )
                      )
                  : undefined
              }
            />
          </div>
        </div>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyRounds]);

  // STORY-028 (#739): the round entry point — a prompt line plus the hashtag
  // link — rendered in two places with identical markup: above the sheet
  // before a flow starts, and again below a completed flow's results as the
  // re-open affordance (Figma 5271-13135).
  //
  // The prompt is the greeting only where it genuinely opens a conversation:
  // above the sheet, with nothing behind the member yet. Below a set of
  // results it always reads as a re-search invitation, and once anything is in
  // history — frozen here, or replayed from a resumed session — so does the
  // one above the sheet. Greeting someone under their own search results, or
  // under their own history, would read as the chat having forgotten them.
  const entryPointPromptKey =
    historyRounds && historyRounds.length > 0
      ? 'ai.scanner.reopen.prompt'
      : 'ai.scanner.greeting';

  // Every 「#ハッシュタグを選択する」 link: the entry point's, and the one under the
  // no-results warning.
  const renderHashtagLink = (onSelectHashtags, options = {}) => (
    <button
      ref={options.linkRef}
      className={classNames('ai-scanner-overlay-hashtag-link', options.className)}
      onClick={onSelectHashtags}
      type="button"
    >
      {localize('ai.scanner.hashtag.link')}
    </button>
  );

  // Wrapped rather than emitted as a bare fragment because the 16px between
  // the prompt and the link used to come from `.ai-scanner-overlay-content`'s
  // flex `gap`, which only reaches its DIRECT children — the copy below the
  // results is nested inside the results block and would render flush. The
  // wrapper carries that gap itself, so both positions space identically.
  const renderEntryPoint = (onSelectHashtags, promptKey, options = {}) => (
    <div className="ai-scanner-overlay-entry-point">
      <p className="ai-scanner-overlay-greeting">{localize(promptKey)}</p>
      {/*
        STORY-024 (#734) AC C20095684: greeting, AI caution, then the hashtag
        action. The caution introduces a FIRST-TIME chat, so it is dropped
        once anything sits above this block — a resumed history, or a flow
        this sitting froze. (#734 keyed that on restored rounds; STORY-028
        (#739) widened the same idea to any history, since a frozen flow is
        just as much "the member has been here already".)
      */}
      {options.withCaution && (
        <p className="ai-scanner-overlay-caution">
          {localize('ai.scanner.caution')}
        </p>
      )}
      {/*
        STORY-024 (#734): `linkRef` is measured for the coach mark's step-2
        spotlight. Only the above-the-sheet instance passes it — the coach
        mark and a completed flow's results are never on screen together.
      */}
      {renderHashtagLink(onSelectHashtags, { linkRef: options.linkRef })}
    </div>
  );

  // --- Pointer-event drag handlers for the bottom sheet ---
  const handleSheetPointerDown = useCallback((e) => {
    const { open, closed } = getSheetSnaps();
    dragRef.current = {
      active: true,
      startY: e.clientY,
      startOffset: sheetExpandedRef.current ? open : closed,
      lastY: e.clientY,
      lastT: performance.now(),
      velocity: 0,
    };
    setSheetDragging(true);
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, [getSheetSnaps]);

  const handleSheetPointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const { open, closed } = getSheetSnaps();
    const now = performance.now();
    const dt = now - dragRef.current.lastT || 16;
    dragRef.current.velocity = (e.clientY - dragRef.current.lastY) / dt;
    dragRef.current.lastY = e.clientY;
    dragRef.current.lastT = now;

    let next = dragRef.current.startOffset + (e.clientY - dragRef.current.startY);
    next = Math.max(open, Math.min(closed, next));
    setSheetOffset(next);
  }, [getSheetSnaps]);

  const handleSheetPointerUp = useCallback((e) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    setSheetDragging(false);

    const { open, closed } = getSheetSnaps();
    const v = dragRef.current.velocity;
    const traveled = Math.abs(e.clientY - dragRef.current.startY);
    // Compute current position from drag data to avoid stale closure on sheetOffset
    const current = dragRef.current.startOffset + (e.clientY - dragRef.current.startY);
    const wasExpanded = sheetExpandedRef.current;

    let willExpand;
    if (traveled < 6) {
      // Barely moved — treat as a tap and toggle.
      willExpand = !wasExpanded;
    } else if (Math.abs(v) > 0.5) {
      // Fast flick wins regardless of position.
      willExpand = v < 0;
    } else {
      // Slow drag — snap to whichever half is closer.
      willExpand = current < (open + closed) / 2;
    }

    // STORY-029 (#740) FR-002/FR-003: expanding the sheet runs the same
    // re-open path the hashtag link does, so a swipe-up after results freezes
    // the flow and starts a new one instead of silently re-running the
    // current conversation. Only the EXPAND direction — routing a collapse
    // through it would freeze the flow on the way out of the sheet.
    if (willExpand) {
      handleOpenHashtagSheet();
    } else {
      updateSheetExpanded(false);
    }
    setSheetOffset(null);
  }, [getSheetSnaps, handleOpenHashtagSheet, updateSheetExpanded]);

  // Escape key collapses the sheet
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && sheetExpanded) {
        updateSheetExpanded(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, sheetExpanded, updateSheetExpanded]);

  // Move focus into the sheet when it expands so keyboard/screen-reader users
  // are aware the content is available. The sheet container itself receives
  // focus (tabIndex={-1} below).
  useEffect(() => {
    if (sheetExpanded) {
      sheetRef.current?.focus();
    }
  }, [sheetExpanded]);

  if (!isFeatureEnabled) {
    return null;
  }

  // Compute bottom sheet translateY
  const { open: snapOpen, closed: snapClosed } = getSheetSnaps();
  const sheetY = sheetOffset ?? (sheetExpanded ? snapOpen : snapClosed);
  const sheetStyle = {
    transform: `translateY(${sheetY}px)`,
    transition: sheetDragging ? 'none' : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
  };

  return (
    <div className="ai-scanner-container">
      {/*
        STORY-024 (#734) AC C20095689: a second entry point beside the
        「スクリーニング」 title, ADDITIONAL to the STORY-022 bottom bar rather
        than a replacement — so it deliberately shares `handleOpen` with it and
        is not tied to `barDismissed`. Dismissing the bar leaves this one.
      */}
      {titleTriggerSlot &&
        createPortal(
          <button
            className="ai-scanner-title-trigger"
            type="button"
            onClick={handleOpen}
            aria-label={localize('ai.scanner.title.trigger.label')}
            aria-expanded={isOpen}
          >
            <img
              className="ai-scanner-title-trigger-logo"
              src={aiScannerHashtag}
              alt=""
              aria-hidden="true"
              width="19"
              height="19"
            />
          </button>,
          titleTriggerSlot
        )}
      {!barDismissed && (
        <div
          className="ai-scanner-bar"
          style={
            barBottomOverride !== null
              ? { bottom: `${barBottomOverride}px` }
              : undefined
          }
        >
          <button
            className="ai-scanner-bar-body"
            type="button"
            onClick={handleOpen}
            aria-label="AIアシスタントを開く"
            aria-expanded={isOpen}
          >
            <span className="ai-scanner-bar-heading">
              <img
                className="ai-scanner-bar-logo"
                src={aiScannerLogo}
                alt=""
                aria-hidden="true"
                width="48"
                height="48"
              />
              <span className="ai-scanner-bar-title">
                <img
                  className="ai-scanner-bar-title-meigara"
                  src={aiScannerMeigara}
                  alt=""
                  aria-hidden="true"
                />
                <img
                  className="ai-scanner-bar-title-mikke"
                  src={aiScannerMikke}
                  alt=""
                  aria-hidden="true"
                />
              </span>
            </span>
            <span className="ai-scanner-bar-subtitle">
              {localize('ai.scanner.bar.subtitle')}
            </span>
          </button>
          <button
            className="ai-scanner-bar-dismiss"
            type="button"
            onClick={handleDismiss}
            aria-label="閉じる"
          >
            <img
              src={closeIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
            />
          </button>
        </div>
      )}
      {isOpen && (
        <div
          className={classNames('ai-scanner-overlay', {
            'ai-scanner-overlay--questions': showQuestions
          })}
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-label="AIスキャナー"
        >
          <button
            className="ai-scanner-overlay-close"
            onClick={handleClose}
            aria-label="閉じる"
          >
            <img
              src={closeIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
            />
          </button>
          {screen === 'welcomeBack' && (
            <WelcomeBackScreen
              onStartFresh={handleStartFresh}
              onResume={handleResume}
            />
          )}
          {screen === 'chat' && (
            <>
              <div className="ai-scanner-overlay-content">
                {/*
              STORY (#725): every round from a resumed session, oldest first,
              read-only — reusing InvestmentQuestions/ScanResults in
              override mode rather than a parallel renderer (FR-008/FR-009).
              `latestHistoryRef` (NOT `resultsRef` — see its declaration)
              lands on the LAST round's own container, so `block: 'start'`
              stops at that round's hashtag-condition header rather than
              scrolling past it to the results card underneath (FR-010).
            */}
                {historyRoundsElements}
                {/*
                  STORY-029 (#740): the same shared open handler as every
                  other trigger, not a bare expand. Safe here even though this
                  position never re-opens anything, and the proof needs no
                  invariant held by hand: this renders only under
                  `!showQuestions`, `hasLiveResults` requires `showQuestions`,
                  so the freeze guard is `false` by direct contradiction and
                  the call is a plain expand. One handler everywhere beats a
                  special case that has to stay true.
                */}
                {!showQuestions &&
                  renderEntryPoint(handleOpenHashtagSheet, entryPointPromptKey, {
                    withCaution: !hasHistoryRounds,
                    linkRef: hashtagLinkRef
                  })}
                {showQuestions && (
                  <div ref={questionsRef} className="ai-scanner-questions-anchor">
                    {/*
                      STORY-015 (#729): every round after the first opens on
                      the re-search prompt instead of Q1 — the NEW selection's
                      condition and count lines, the previous round's answers
                      frozen underneath them, and
                      「はい」/「いいえ」. The live questions are reached only via
                      「いいえ」, so the member is never re-asked what they were
                      just offered the chance to keep.

                      Both the recap and the live questions read the CURRENT
                      selection's tags and count: the new round searches what
                      the member has just chosen in the sheet, and only the
                      Q1/Q2 pair comes from the previous round.

                      `count !== 0` drops the prompt when there is nothing to
                      search and falls through to the ordinary questions step,
                      which owns the 「条件に一致する銘柄が見つかりませんでした」
                      warning. The recap cannot show it: that warning is
                      suppressed under `readOnly` (a stored round's count is
                      history, not a live zero), so a member who empties the
                      selection while the prompt is up would otherwise get an
                      empty 「「」の条件で探しました」 line, 「0 件」, and a 「はい」
                      that searches nothing — the state #737 AC C20108249
                      removed. `null` is loading and deliberately keeps the
                      prompt up.
                    */}
                    {reSearchFrom && filterResult.count !== 0 ? (
                      <>
                        <InvestmentQuestions
                          readOnly
                          instanceId={RE_SEARCH_INSTANCE_ID}
                          overrideAnswers={reSearchFrom}
                          selectedTags={selectedTagLabels}
                          matchCount={filterResult.count}
                          prompt={
                            <p id={RE_SEARCH_PROMPT_ID}>
                              {localize('ai.scanner.research.prompt')}
                            </p>
                          }
                        >
                          <ReSearchOptions
                            value={reSearchChoice}
                            onSelect={handleReSearchChoice}
                            labelledBy={RE_SEARCH_PROMPT_ID}
                            instanceId={RE_SEARCH_INSTANCE_ID}
                          />
                        </InvestmentQuestions>
                        {/*
                          `hideHeader`: the recap directly above has already
                          rendered the condition and count for this very
                          selection, and repeating them would claim the search
                          ran twice.
                        */}
                        {isChangingConditions && (
                          <InvestmentQuestions
                            hideHeader
                            selectedTags={selectedTagLabels}
                            matchCount={filterResult.count}
                            onAnswersComplete={handleAnswersComplete}
                          />
                        )}
                      </>
                    ) : (
                      <InvestmentQuestions
                        selectedTags={selectedTagLabels}
                        matchCount={filterResult.count}
                        onAnswersComplete={handleAnswersComplete}
                      >
                        {/*
                          The warning asks the member to pick hashtags again,
                          and no other entry point is on screen while it shows.
                          Left off under saved rounds by product decision.
                        */}
                        {filterResult.count === 0 &&
                          !hasHistoryRounds &&
                          renderHashtagLink(handleOpenHashtagSheet, {
                            className: 'ai-scanner-questions-warning-link'
                          })}
                      </InvestmentQuestions>
                    )}
                    {/*
                  Clearing the hashtag selection (or losing the scans to a
                  load/error) empties the filtered set, and the step falls back
                  to the STORY-009 warning on its own. Nothing from the results
                  step should survive that: a confirmation message and a
                  「0 件 見つかりました！」 under a "no matches" warning is a
                  contradiction, and NFR 13 puts the floor at 3 anyway.
                */}
                    {hasLiveResults && (
                      <div ref={resultsRef}>
                        {/*
                      Keyed on the hashtag selection so changing the search
                      rebuilds the step and drops the ticked cards: the ticks
                      answered the previous question, and carrying them into a
                      new candidate list would show cards ticked that the
                      member never ticked here. Deliberately NOT keyed on the
                      symbol list — a background scan refresh can change that
                      with no member action, and it must not discard their
                      selection.
                    */}
                        <ScanResults
                          key={Array.from(selectedHashtagIds).sort().join()}
                          candidates={liveCandidates}
                          onSelectionChange={handleLiveSelectionChange}
                        />
                        {/*
                      STORY-028 (#739) FR-002: the same entry point again,
                      below the AI disclaimer, now reading as a re-search.
                      Rendered inside the results block so it appears only
                      once a flow has actually produced results — and so that
                      freezing the flow leaves it in the same place on screen,
                      directly under the round that just became history.
                    */}
                        {renderEntryPoint(
                          handleOpenHashtagSheet,
                          'ai.scanner.reopen.prompt'
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div
                className={classNames('ai-scanner-overlay-swipe-area', {
                  'ai-scanner-overlay-swipe-area--expanded': sheetExpanded
                })}
                ref={sheetRef}
                style={{ '--sheet-top-margin': `${SHEET_TOP_MARGIN}px`, ...sheetStyle }}
                tabIndex={-1}
                role={sheetExpanded ? 'region' : undefined}
                aria-labelledby={sheetExpanded ? 'ai-scanner-sheet-title' : undefined}
              >
                <div
                  className="ai-scanner-overlay-swipe-area-handle"
                  onPointerDown={handleSheetPointerDown}
                  onPointerMove={handleSheetPointerMove}
                  onPointerUp={handleSheetPointerUp}
                  onPointerCancel={handleSheetPointerUp}
                  role="button"
                  tabIndex={0}
                  aria-expanded={sheetExpanded}
                  aria-label={sheetExpanded ? localize('button.label.close') : localize('ai.scanner.hashtag.modal.open')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      // STORY-029 (#740): same split as the pointer path —
                      // the keyboard toggle must not be a second way to open
                      // the sheet that skips the re-open.
                      if (sheetExpanded) {
                        updateSheetExpanded(false);
                      } else {
                        handleOpenHashtagSheet();
                      }
                    }
                  }}
                >
                  <div className="ai-scanner-overlay-swipe-area-grabber" />
                </div>
                <div
                  className="ai-scanner-overlay-swipe-area-body"
                  aria-hidden={!sheetExpanded}
                  style={{
                    opacity: sheetExpanded || sheetOffset !== null ? 1 : 0,
                    overflowY: 'hidden',
                  }}
                >
                  <div className="ai-scanner-overlay-swipe-area-content">
                    <p id="ai-scanner-sheet-title" className="ai-scanner-overlay-swipe-area-title">
                      {localize('ai.scanner.hashtag.modal.title')}
                    </p>
                    <HashtagChips
                      selectedIds={selectedHashtagIds}
                      onSelectionChange={handleHashtagSelectionChange}
                      onShowResults={handleShowResults}
                      matchCount={filterResult.count}
                      isLoading={filterResult.isLoading}
                      hasError={filterResult.hasError}
                    />
                  </div>
                </div>
              </div>
              {showCoachMark && (
                <AiScannerCoachMark
                  onComplete={handleCoachMarkComplete}
                  hashtagTargetY={hashtagTargetY}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AiScannerContainer;
